import type { ReactElement } from 'react';
import { Flex } from '@chakra-ui/react';
import { InformationMessage } from '../../../components/information-message/InformationMessage';
import { Header, Text } from '../../../ui-components';
import { useCustomPreferenceOrDefault } from '../../../utils/api';
import { CustomPreference } from '../../../utils/api/CustomPreference';

export interface CartSelectionProps {
    id: string;
    title: string;
    children: React.ReactNode | React.ReactNode[];
    address?: string;
    displayInformationMessage?: boolean;
}

export const CartSection = ({
    id,
    title,
    address,
    children,
    displayInformationMessage = false,
}: CartSelectionProps): ReactElement => {
    const informationMessageControl = useCustomPreferenceOrDefault(
        CustomPreference.ENABLE_INFORMATION_MESSAGE_CART_AND_CHECKOUT,
        false,
    );

    const isInformationTextVisible = displayInformationMessage && informationMessageControl;

    return (
        <Flex
            direction="column"
            background="white"
            boxShadow="0px 2px 10px 0px rgba(65, 64, 66, 0.14)"
            borderRadius={{ base: 0, md: 16 }}
            data-testid={id}
        >
            <Flex
                padding={{ base: 4, sm: '1rem 2rem', md: '1rem' }}
                borderBottom="1px solid #B3B3B3"
                direction="column"
                gap={2}
            >
                <Header
                    id={`${id}-title`}
                    as="h4"
                    variant="header4"
                    style="bold"
                    color="slt-pepper.1000"
                >
                    {title}
                </Header>
                {isInformationTextVisible && <InformationMessage />}
                {address && <Text id={`${id}-address`}>{address}</Text>}
            </Flex>
            <Flex
                data-testid={`${id}-items`}
                direction="column"
                padding={{ base: '1rem', sm: '2rem', md: '1rem' }}
            >
                {children}
            </Flex>
        </Flex>
    );
};

CartSection.defaultProps = {
    address: '',
    displayInformationMessage: false,
};
